import { Button } from "@/components/ui/button";
import { h1 } from "@/components/zthd/Title";
import { cn } from "@/lib/utils";
import { postFetcher, tokenFetcher, useAuthStore } from "@/lib/swr";
import { useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import useSWRMutation from "swr/mutation";
import { ListLoading } from "@/components/list/status-text";
import { FEISHU_APP_ID } from "@/constants/feishu";

// CLI login (wed skill): bind the feishu OAuth code to the CLI-generated
// session id via POST /login/cli — the terminal polls the server to collect
// it, so nothing is displayed for manual copy and no browser session is kept.
const CliWithCode = ({ code, session }: { code: string; session: string }) => {
  const { trigger, data, error, isMutating } = useSWRMutation(
    "/login/cli",
    postFetcher<{ ok?: boolean }>
  );

  useEffect(() => {
    if (code && session) {
      trigger({ code, session });
    }
  }, [code, session, trigger]);

  return (
    <div className="flex flex-col mx-auto justify-center items-center min-h-screen gap-4">
      <h1 className={cn(h1)}>CLI login</h1>
      {isMutating && <ListLoading>Signing in…</ListLoading>}
      {error && (
        <p className="text-destructive">
          Login failed. Close this page and retry from your terminal.
        </p>
      )}
      {data?.ok && (
        <>
          <p className="text-xl">Login complete.</p>
          <p className="text-muted-foreground text-sm max-w-md text-center">
            You can close this page and return to your terminal — the CLI will
            pick up the session automatically (within 10 minutes).
          </p>
        </>
      )}
    </div>
  );
};

// Native App login (ccp): trade the feishu code for a one-time app code bound
// to the App's PKCE challenge, then bounce back to the App via the server's
// whitelisted redirect (e.g. ccp://auth). The App exchanges code+verifier.
const AppWithCode = ({
  code,
  client,
  appState,
  challenge,
}: {
  code: string;
  client: string;
  appState: string;
  challenge: string;
}) => {
  const { trigger, data, error, isMutating } = useSWRMutation(
    "/login/app",
    postFetcher<{ code: string; redirect: string }>
  );

  useEffect(() => {
    trigger({ code, client, challenge });
  }, [code, client, challenge, trigger]);

  const backUrl = data
    ? `${data.redirect}?code=${encodeURIComponent(
        data.code
      )}&state=${encodeURIComponent(appState)}`
    : null;

  useEffect(() => {
    if (backUrl) window.location.href = backUrl;
  }, [backUrl]);

  return (
    <div className="flex flex-col mx-auto justify-center items-center min-h-screen gap-4">
      <h1 className={cn(h1)}>App login</h1>
      {isMutating && <ListLoading>Signing in…</ListLoading>}
      {error && (
        <p className="text-destructive">
          Login failed. Close this page and retry from {client}.
        </p>
      )}
      {backUrl && (
        <>
          <p className="text-xl">Login complete.</p>
          <p className="text-muted-foreground text-sm max-w-md text-center">
            Returning to {client}. If nothing happens, open it below; you can
            close this page afterwards.
          </p>
          <Button asChild>
            <a href={backUrl}>Open {client}</a>
          </Button>
        </>
      )}
    </div>
  );
};

const WithCode = ({ code, from }: { code: string; from: string }) => {
  const { setToken } = useAuthStore();
  const navigate = useNavigate();
  const { trigger, isMutating } = useSWRMutation("/login", tokenFetcher, {
    onSuccess: (data) => {
      if (data) {
        setToken(data);
        navigate(from);
      }
    },
    onError: () => {
      navigate("/500");
    },
  });

  useEffect(() => {
    if (code) {
      trigger({ code });
    }
  }, [code, trigger]);

  return (
    <div>
      <p>{code}</p>
      {isMutating && <ListLoading>Logging in…</ListLoading>}
    </div>
  );
};

const LoginPage = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryParams = Object.fromEntries(searchParams.entries());
  if (queryParams.code) {
    // state=cli:<session> marks the wed-skill CLI flow (normal web logins
    // carry a "from" path in state, which always starts with "/").
    // state=app:<client>:<appState>:<challenge> marks the native App flow.
    const appParts = queryParams.state?.split(":");
    if (appParts?.[0] === "app" && appParts.length === 4) {
      return (
        <AppWithCode
          code={queryParams.code}
          client={appParts[1]}
          appState={appParts[2]}
          challenge={appParts[3]}
        />
      );
    }
    if (queryParams.state?.startsWith("cli:")) {
      return (
        <CliWithCode
          code={queryParams.code}
          session={queryParams.state.slice("cli:".length)}
        />
      );
    }
    return (
      <div className="flex flex-col max-auto items-center">
        <WithCode
          code={queryParams.code}
          from={queryParams.state?.startsWith("/") ? queryParams.state : "/"}
        />
      </div>
    );
  }

  // ?cli=<session>: entry opened from the wed-skill login script; thread the
  // CLI session id through the feishu OAuth state round-trip.
  const cliSession = queryParams.cli;
  // ?client=<app>&state=<s>&challenge=<c>: entry opened by a native App (ccp);
  // all three ride the feishu state round-trip.
  const appLogin =
    queryParams.client && queryParams.state && queryParams.challenge
      ? `app:${queryParams.client}:${queryParams.state}:${queryParams.challenge}`
      : null;
  // Keep search & hash so entries like /e/:id?follow=1 survive the login
  // round-trip (the auth guard passes the full location in state.from).
  const from = location.state?.from
    ? `${location.state.from.pathname}${location.state.from.search || ""}${
        location.state.from.hash || ""
      }`
    : "/";
  const appId = FEISHU_APP_ID;
  const redirect_uri = `${window.location.origin}/login`;
  const href = `https://open.feishu.cn/open-apis/authen/v1/index?redirect_uri=${encodeURI(
    redirect_uri
  )}&app_id=${appId}&state=${encodeURIComponent(
    appLogin ?? (cliSession ? `cli:${cliSession}` : from)
  )}`;

  return (
    <div className="flex flex-col mx-auto justify-center items-center min-h-screen">
      <h1 className={cn("my-5", h1)}>Welcome to Wednesday.</h1>
      <Button asChild>
        <a href={href}>Login use feishu</a>
      </Button>
    </div>
  );
};

export default LoginPage;
